import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useFileStore, useLightboxStore, useModelStore, useChatInputStore, useAuthStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { useRouter } from 'expo-router';
import { BORDER_RADIUS, DEFAULT_MUSIC_MODEL_SLUG } from '@/constants/config';

const createStyles = (colors, isXS, isXL) => StyleSheet.create({
  container: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  grid: {
    ...(Platform.OS === 'web' ? {
      display: 'grid',
      gridTemplateColumns: isXS ? '1fr' : isXL ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
      gap: isXS ? 8 : 12,
    } : {
      flexDirection: isXS ? 'column' : 'row',
      flexWrap: 'wrap',
      gap: isXS ? 8 : 12,
    }),
  },
  gridItem: {
    ...(Platform.OS !== 'web' ? (isXS ? {
      width: '100%',
    } : {
      width: isXL ? '32%' : '48.5%',
    }) : {}),
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 20,
    alignItems: 'center',
  },
});

const musicItemStyles = (colors, isHovered, isAltBGColor) => StyleSheet.create({
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: BORDER_RADIUS.medium || 12,
    backgroundColor: isHovered ? (isAltBGColor ? colors.solid_color_secondary_dark : colors.solid_color_tertiary) : (isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block),
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    gap: 12,
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' }),
  },
  thumbnailContainer: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    ...(Platform.OS === 'web' ? { minWidth: 0 } : {}),
    gap: 4,
  },
  title: {
    lineHeight: 20,
    color: colors.text_color_primary,
    flexShrink: 1,
  },
  duration: {
    opacity: 0.7,
  },
});

const MusicGalleryItem = React.memo(({ file, onPress }) => {
  const { colors, theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(() => musicItemStyles(colors, isHovered, isAltBGColor), [colors, isHovered, isAltBGColor]);

  // Use thumbnailPath or a default music icon
  const thumbnailUrl = file?.thumbnailPath || file?.coverImage;
  const fileName =
    file?.title ||
    file?.fileName ||
    file?.parameters?.description ||
    file?.name ||
    (typeof file?.parameters?.prompt === 'string' ? file.parameters.prompt : null) ||
    'Untitled Track';
  const duration = file?.duration ? formatDuration(file.duration) : '';

  return (
    <Pressable 
      style={styles.item} 
      onPress={() => onPress?.(file)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={styles.thumbnailContainer}>
        {thumbnailUrl ? (
          <Image
            source={{ uri: thumbnailUrl }}
            style={[styles.thumbnail, { backgroundColor: colors.solid_color_tertiary }]}
            contentFit="cover"
            cachePolicy="memory-disk"
            priority="high"
            transition={150}
          />
        ) : (
          <Icon name="music" size={24} color={colors.text_color_tertiary} />
        )}
      </View>
      <View style={styles.textContainer}>
        <Text size="s" weight="medium" color="primary" numberOfLines={2} style={styles.title}>
          {fileName}
        </Text>
        {duration && (
          <Text size="xs" color="tertiary" style={styles.duration}>
            {duration}
          </Text>
        )}
      </View>
      <Icon name="play" size={20} color={colors.text_color_secondary} />
    </Pressable>
  );
});

// Helper to format duration in seconds to MM:SS
const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const MusicGallery = () => {
  const { colors } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const { t } = useTranslation();
  const router = useRouter();
  
  const { openLightbox } = useLightboxStore(state => state.actions);
  
  // File store
  const files = useFileStore(state => state.files);
  const generatedMusicIds = useFileStore(state => state.generatedMusicIds);
  const isLoading = useFileStore(state => state.isLoading);
  const errors = useFileStore(state => state.errors);
  const pagination = useFileStore(state => state.pagination);
  
  const { fetchGeneratedMusic } = useFileStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();

  const styles = useMemo(() => createStyles(colors, isXS, isXL), [colors, isXS, isXL]);

  // The "My Music" gallery is per-user. Skip the fetch entirely for
  // anonymous visitors (would 401) and surface a sign-in CTA empty state
  // below in place of the library.
  useEffect(() => {
    if (hasUser) fetchGeneratedMusic(1);
  }, [fetchGeneratedMusic, hasUser]);

  // Get the music files from IDs
  const musicFiles = useMemo(() => {
    return generatedMusicIds.map(id => files[id]).filter(Boolean);
  }, [files, generatedMusicIds]);

  const handleMusicPress = useCallback((file) => {
    if (file?.cdnPath) {
      openLightbox({
        imageUrl: file.cdnPath,
        fileId: file.id,
        openToEditor: false,
        withInpainting: false,
        withChatInput: false,
      });
    }
  }, [openLightbox]);

  const handleLoadMore = useCallback(async () => {
    const currentPagination = pagination.generatedMusic;
    if (!currentPagination?.hasMore || isLoading) return;
    
    const nextPage = (currentPagination?.page || 1) + 1;
    await fetchGeneratedMusic(nextPage);
  }, [pagination, isLoading, fetchGeneratedMusic]);

  const handleCreateFirst = useCallback(async () => {
    const ran = requireAuth(() => true, {
      source: 'music_gallery_create_first',
      title: 'Sign in to make music',
      subtitle: 'Music generations are billed to your account so we can apply your credits and save the result.',
      icon: 'music',
    });
    if (ran !== true) return;
    const musicModel = await useModelStore.getState().resolveDefaultOutputModel('create-music', DEFAULT_MUSIC_MODEL_SLUG, { force: true });
    
    if (musicModel?.id) {
      // Initialize music generation with the model pre-selected
      useChatInputStore.getState().initializeMusicGeneration('', musicModel.id, false);
    }
    
    router.push('/app');
  }, [router, requireAuth]);

  // Show loading state
  if (isLoading && musicFiles.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.grid}>
          {[...Array(4)].map((_, index) => (
            <View key={index} style={styles.gridItem}>
              <View
                style={{
                  height: 80,
                  borderRadius: BORDER_RADIUS.medium || 12,
                  backgroundColor: colors.solid_color_secondary,
                }}
              />
            </View>
          ))}
        </View>
      </View>
    );
  }

  // Show error state
  if (errors?.generatedMusic) {
    return (
      <View style={styles.container}>
        <StatusContainer
          type="error"
          message={errors.generatedMusic}
          actionText="Retry"
          onAction={() => fetchGeneratedMusic(1)}
        />
      </View>
    );
  }

  // Show empty state — anonymous variant explains why the library is
  // empty (vs the generic "no music yet" copy for authed users).
  if (musicFiles.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <Icon 
            name="music" 
            size={48} 
            color={colors.text_color_tertiary} 
            style={{ marginBottom: 16 }}
          />
          <Text size="m" weight="medium" color="secondary" style={{ marginBottom: 4 }}>
            {hasUser ? t('music.noMusic') : 'Sign in to see your music'}
          </Text>
          <Text size="s" color="tertiary" style={{ marginBottom: 20, textAlign: 'center' }}>
            {hasUser
              ? t('music.noMusicSubtitle')
              : 'Create a free account so we can save every track you generate to your library.'}
          </Text>
          <Button
            text={hasUser ? t('music.createFirst') : 'Sign up free'}
            variant="primary"
            size="medium"
            prependIcon={hasUser ? 'sparkles' : 'user-plus'}
            onPress={handleCreateFirst}
          />
        </View>
      </View>
    );
  }

  const hasMore = pagination?.generatedMusic?.hasMore;

  return (
    <View style={styles.container}>
      <View style={styles.grid}>
        {musicFiles.map((file) => (
          <View key={file.id} style={styles.gridItem}>
            <MusicGalleryItem
              file={file}
              onPress={handleMusicPress}
            />
          </View>
        ))}
      </View>
      {hasMore && (
        <View style={styles.loadMoreContainer}>
          <LoadMoreButton
            isLarge
            style={{width: '100%'}}
            onPress={handleLoadMore}
            isLoading={isLoading}
          />
        </View>
      )}
    </View>
  );
};

export default React.memo(MusicGallery);

